/**
 * @fileoverview Media Preview Plugin for CKEditor 5
 * 
 * This module provides CKEditor 5 plugins for handling media preview components
 * and upload failed components. It includes:
 * - MediaPreview: Plugin for media preview widgets
 * - UploadFailedTag: Plugin for failed upload widgets
 * - Commands for inserting components
 * - Utility functions for element creation and validation
 * 
 * @author Arbo Collaboration
 * @version 2.0.0
 */

import {
  Command,
  Plugin,
  Widget,
  toWidget,
  type ViewDowncastWriter as DowncastWriter,
  type UpcastConversionApi,
  type ViewElement,
} from "../ckeditor-plugins";
import {
  acceptAttribute,
  PREVIEW_COMPONENT,
  UPLOAD_FAILED,
  type AttributeType,
} from "../constants";
import { iteratorToObject } from "../utils";

/**
 * Interface for component properties
 */
interface ComponentProps {
  id: string;
  path: string;
  [key: string]: unknown;
}

/**
 * Interface for component attributes
 */
interface ComponentAttributes extends Record<string, unknown> {
  id?: string;
  path?: string;
  name?: string;
  fileid?: string;
  mimetype?: string;
  thumbnail?: string;
}

/**
 * Base command class for media components
 */
abstract class BaseMediaCommand extends Command {
  /**
   * Executes the command to insert a media component
   * @param elementName - The name of the element to create
   * @param props - The properties for the component
   */
  protected executeCommand(elementName: string, props: ComponentProps): void {
    if (!props?.fileid && !props?.path) {
      console.warn(`${this.constructor.name}: Invalid props provided`, props);
      return;
    }

    const model = this.editor.model;
    
    model.change((writer) => {
      const element = writer.createElement(elementName, props);
      model.insertContent(element);
    });
  }
}

/**
 * Command for inserting preview components
 */
export class PreviewComponentCommand extends BaseMediaCommand {
  public execute(props: ComponentProps): void {
    this.executeCommand(PREVIEW_COMPONENT, props);
  }
}

/**
 * Command for inserting failed upload components
 */
export class FailedUploadComponentCommand extends BaseMediaCommand {
  public execute(props: ComponentProps): void {
    this.executeCommand(UPLOAD_FAILED, props);
  }
}

// Type declarations for better TypeScript support
declare module "@ckeditor/ckeditor5-core" {
  interface CommandsMap {
    Preview: MediaPreview;
    UploadFailed: UploadFailedTag;
  }
}

/**
 * Plugin configuration constants
 */
const PLUGIN_CONFIG = {
  WIDGET_LABELS: {
    PREVIEW: "Preview Component Widget",
    UPLOAD_FAILED: "Upload Failed Component Widget",
  },
  PLUGIN_NAMES: {
    PREVIEW: "MediaPreviewPlugin",
    UPLOAD_FAILED: "UploadFailedPlugin",
  },
  SCHEMA: {
    PREVIEW: {
      allowWhere: "$text",
      allowAttributesOf: "$text",
      isInline: true,
      isObject: true,
      allowAttributes: [...acceptAttribute],
    },
    UPLOAD_FAILED: {
      allowWhere: "$text", 
      allowAttributesOf: "$text",
      isInline: true,
      isObject: true,
      allowAttributes: ["id", "name", "path"],
    },
  },
} as const;

/**
 * Base class for media plugins to reduce code duplication
 */
abstract class BaseMediaPlugin extends Plugin {
  protected abstract getElementName(): string;
  protected abstract getPluginName(): string;
  protected abstract getWidgetLabel(): string;
  protected abstract getSchemaConfig(): any;
  protected abstract getCommandClass(): new (editor: any) => Command;

  public static get requires() {
    return [Widget];
  }

  /**
   * Initialize the plugin
   */
  public init(): void {
    this._defineSchema();
    this._defineConverters();
    this._addCommands();
  }

  /**
   * Define the schema for the component
   * @private
   */
  private _defineSchema(): void {
    const elementName = this.getElementName();
    const schemaConfig = this.getSchemaConfig();
    
    this.editor.model.schema.register(elementName, schemaConfig);
  }

  /**
   * Add commands to the editor
   * @private
   */
  private _addCommands(): void {
    const elementName = this.getElementName();
    const CommandClass = this.getCommandClass();
    
    this.editor.commands.add(elementName, new CommandClass(this.editor));
  }

  /**
   * Create upcast converter
   * @private
   */
  private _createUpcaster() {
    const elementName = this.getElementName();
    
    return (viewElement: ViewElement, conversionApi: UpcastConversionApi) => {
      try {
        const attributes = iteratorToObject(viewElement.getAttributes());
        return conversionApi.writer.createElement(elementName, attributes);
      } catch (error) {
        console.error(`Error in ${elementName} upcast converter:`, error);
        return conversionApi.writer.createElement(elementName);
      }
    };
  }

  /**
   * Create editing downcast converter
   * @private
   */
  private _createEditingDowncaster() {
    const elementName = this.getElementName();
    const widgetLabel = this.getWidgetLabel();
    
    return (modelElement: any, { writer }: { writer: DowncastWriter }) => {
      try {
        const attributes = iteratorToObject(modelElement.getAttributes());
        const viewElement = writer.createContainerElement("span", {
          class: `${elementName}-widget`,
        }, [
          writer.createAttributeElement(elementName, attributes),
        ]);
        
        return toWidget(viewElement, writer, {
          label: widgetLabel,
          hasSelectionHandle: true,
        });
      } catch (error) {
        console.error(`Error in ${elementName} editing downcast converter:`, error);
        const fallbackElement = writer.createContainerElement("span", {
          class: `${elementName}-widget-error`,
        });
        return toWidget(fallbackElement, writer, { label: widgetLabel });
      }
    };
  }

  /**
   * Create data downcast converter
   * @private
   */
  private _createDataDowncaster() {
    const elementName = this.getElementName();
    
    return (modelElement: any, { writer }: { writer: DowncastWriter }) => {
      try {
        const attributes = iteratorToObject(modelElement.getAttributes());
        return writer.createEmptyElement(elementName, attributes);
      } catch (error) {
        console.error(`Error in ${elementName} data downcast converter:`, error);
        return writer.createEmptyElement(elementName);
      }
    };
  }

  /**
   * Define converters for the component
   * @private
   */
  private _defineConverters(): void {
    const elementName = this.getElementName();
    const conversion = this.editor.conversion;

    // Upcast converter: HTML to model
    conversion.for("upcast").elementToElement({
      view: elementName,
      model: this._createUpcaster(),
    });

    // Editing downcast converter: model to editing view
    conversion.for("editingDowncast").elementToElement({
      model: elementName,
      view: this._createEditingDowncaster(),
    });

    // Data downcast converter: model to data view
    conversion.for("dataDowncast").elementToElement({
      model: elementName,
      view: this._createDataDowncaster(),
    });
  }
}

/**
 * MediaPreview Plugin for CKEditor 5
 * Handles preview components in the editor
 */
export class MediaPreview extends BaseMediaPlugin {
  public static get pluginName(): string {
    return PLUGIN_CONFIG.PLUGIN_NAMES.PREVIEW;
  }

  protected getElementName(): string {
    return PREVIEW_COMPONENT;
  }

  protected getPluginName(): string {
    return PLUGIN_CONFIG.PLUGIN_NAMES.PREVIEW;
  }

  protected getWidgetLabel(): string {
    return PLUGIN_CONFIG.WIDGET_LABELS.PREVIEW;
  }

  protected getSchemaConfig(): any {
    return PLUGIN_CONFIG.SCHEMA.PREVIEW;
  }

  protected getCommandClass(): new (editor: any) => Command {
    return PreviewComponentCommand;
  }
}

/**
 * UploadFailedTag Plugin for CKEditor 5
 * Handles failed upload components in the editor
 */
export class UploadFailedTag extends BaseMediaPlugin {
  public static get pluginName(): string {
    return PLUGIN_CONFIG.PLUGIN_NAMES.UPLOAD_FAILED;
  }

  protected getElementName(): string {
    return UPLOAD_FAILED;
  }

  protected getPluginName(): string {
    return PLUGIN_CONFIG.PLUGIN_NAMES.UPLOAD_FAILED;
  }

  protected getWidgetLabel(): string {
    return PLUGIN_CONFIG.WIDGET_LABELS.UPLOAD_FAILED;
  }

  protected getSchemaConfig(): any {
    return PLUGIN_CONFIG.SCHEMA.UPLOAD_FAILED;
  }

  protected getCommandClass(): new (editor: any) => Command {
    return FailedUploadComponentCommand;
  }
}

/**
 * Utility functions for creating media elements
 */

/**
 * Creates a preview element with proper error handling
 * @param writer - The downcast writer
 * @param attributes - The attributes for the element
 * @returns The created preview element
 */
export const createPreviewElement = (
  writer: DowncastWriter,
  attributes: ComponentAttributes
) => {
  try {
    // Validate required attributes
    if (!attributes.id || !attributes.path) {
      console.warn("createPreviewElement: Missing required attributes (id, path)");
    }
    
    return writer.createEmptyElement(PREVIEW_COMPONENT, attributes);
  } catch (error) {
    console.error("Error creating preview element:", error);
    return writer.createEmptyElement(PREVIEW_COMPONENT, { id: "error", path: "" });
  }
};

/**
 * Creates an upload failed element with proper error handling
 * @param writer - The downcast writer
 * @param attributes - The attributes for the element
 * @returns The created upload failed element
 */
export const createUploadFailedElement = (
  writer: DowncastWriter,
  attributes: ComponentAttributes
) => {
  try {
    // Validate required attributes
    if (!attributes.id || !attributes.name) {
      console.warn("createUploadFailedElement: Missing required attributes (id, name)");
    }
    
    return writer.createEmptyElement(UPLOAD_FAILED, attributes);
  } catch (error) {
    console.error("Error creating upload failed element:", error);
    return writer.createEmptyElement(UPLOAD_FAILED, { id: "error", name: "failed" });
  }
};

/**
 * Validates component properties
 * @param props - The properties to validate
 * @param requiredFields - Array of required field names
 * @returns True if valid, false otherwise
 */
export const validateComponentProps = (
  props: ComponentProps,
  requiredFields: string[] = ["id", "path"]
): boolean => {
  if (!props || typeof props !== "object") {
    return false;
  }

  return requiredFields.every(field => 
    props[field] && typeof props[field] === "string" && props[field].trim().length > 0
  );
};
